// ambientBrain.js — sprint apolo-brain-vivo (AMB1).
//
// O vault entra SOZINHO em turnos relevantes do Code chat: gate puro decide,
// dedupe por sessão evita repetição, e o bloco é SEMPRE bounded (≤1500 —
// menor que o cap do tool: é contexto ambiente, não resposta). O wiring
// (maybeAmbientBrain) NUNCA lança: qualquer erro = no-op silencioso — um
// índice quebrado jamais derruba um turno de chat (arch:ambient-never-throws).
// Decisão é PURA (shouldInject/selectHits/buildAmbientBlock); I/O entra por
// queryFn injetado (default: window.apolo.corpus.query).

export const AMBIENT_CAP = 1500;
export const AMBIENT_MIN_SCORE = 0.55;
const MIN_TURN_CHARS = 20;
const STORAGE_KEY = 'apolo.ambientBrain';

// hash leve do chunk pro dedupe de sessão (path+heading basta — estável)
const chunkKey = (h) => `${h.path}|${h.heading}`;

// estado por sessão (processo do renderer vive a sessão inteira)
const sessionSeen = new Map(); // sessionId -> Set(chunkKey)

export function resetAmbientSession(sessionId) {
  sessionSeen.set(sessionId, new Set());
}

export function isAmbientEnabled() {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    return raw == null ? true : raw === 'true'; // default ON (D-3)
  } catch {
    return true;
  }
}

export function setAmbientEnabled(on) {
  try { globalThis.localStorage?.setItem(STORAGE_KEY, String(!!on)); } catch { /* sem storage */ }
}

// Gate PURO: injeta só em turno substantivo com o toggle ligado.
export function shouldInject(turnText, { enabled } = {}) {
  if (!enabled) return false;
  const text = String(turnText || '').trim();
  if (text.length < MIN_TURN_CHARS) return false;
  if (text.startsWith('/') || text.startsWith('!')) return false; // comando/slash
  return true;
}

// Filtro: score mínimo + dedupe contra o que a sessão já viu. O `seen` é um
// acumulador explícito: hit selecionado é MARCADO aqui (dedupe autocontido —
// o orquestrador não tem como esquecer de marcar).
export function selectHits(results, { minScore = AMBIENT_MIN_SCORE, seen } = {}) {
  const out = [];
  for (const h of results || []) {
    if (typeof h?.score !== 'number' || h.score < minScore) continue;
    const key = chunkKey(h);
    if (seen?.has(key)) continue;
    seen?.add(key);
    out.push(h);
  }
  return out;
}

// Montagem PURA, SEMPRE ≤ cap.
export function buildAmbientBlock(hits, { cap = AMBIENT_CAP } = {}) {
  if (!hits || hits.length === 0) return '';
  const lines = ['[BRAIN · contexto do vault] Trechos do conhecimento pessoal do usuário, use se relevante:'];
  for (const h of hits) {
    const head = h.heading && !String(h.heading).startsWith('∅') ? ` ${h.heading}` : '';
    lines.push(`— ${h.title || h.path} (${h.path}${head}): ${String(h.chunk || '').trim()}`);
  }
  const out = lines.join('\n');
  return out.length > cap ? `${out.slice(0, cap - 1)}…` : out;
}

// Wiring: decide → consulta → seleciona → marca como visto → monta. Nunca lança.
export async function maybeAmbientBrain(turnText, sessionId, { enabled, queryFn, k = 3 } = {}) {
  try {
    if (!shouldInject(turnText, { enabled })) return '';
    const query = typeof queryFn === 'function'
      ? queryFn
      : (typeof window !== 'undefined' ? window.apolo?.corpus?.query : null);
    if (typeof query !== 'function') return '';
    const res = await query({ query: String(turnText).trim().slice(0, 500), k });
    if (!res?.ok || !Array.isArray(res.results) || res.results.length === 0) return '';
    if (!sessionSeen.has(sessionId)) sessionSeen.set(sessionId, new Set());
    const seen = sessionSeen.get(sessionId);
    const hits = selectHits(res.results, { seen });
    if (hits.length === 0) return '';
    return buildAmbientBlock(hits);
  } catch {
    return ''; // LEI: ambiente nunca quebra um turno
  }
}
